import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Switch } from "../../../components/ui/switch";
import {
  SimpleModal,
  SimpleModalDescription,
  SimpleModalFooter,
  SimpleModalHeader,
  SimpleModalTitle,
} from "../../../components/ui/simple-modal";
import {
  grantAvailabilityShare,
  revokeAvailabilityShare,
  updateAvailabilityShare,
} from "../../../lib/data/availability-shares";
import { getUserLabel } from "../../../lib/identity";

/**
 * Phase 4 S5 — owner-side grant/manage/revoke modal for a single friend.
 *
 * Two modes:
 *   - "grant"  → no active share exists; the [Share] action creates one
 *   - "manage" → an active share exists; user can save the toggle change
 *                or revoke
 *
 * The single user-facing toggle is `redactMarkTitles`. Busy windows are
 * always redacted by the projection — the modal is intentionally narrow in
 * MVP so future per-calendar / per-group controls can land additively.
 */
export function AvailabilityShareModal({
  open,
  onOpenChange,
  ownerId,
  friend,
  existingShare,
}) {
  const mode = existingShare ? "manage" : "grant";
  const [redactMarkTitles, setRedactMarkTitles] = useState(false);
  const [busy, setBusy] = useState(false);

  // Hydrate the toggle from the existing share whenever the modal opens or
  // we transition between grant/manage modes for this friend.
  useEffect(() => {
    if (!open) return;
    setRedactMarkTitles(Boolean(existingShare?.redactMarkTitles));
  }, [open, existingShare]);

  // Use the friend's chosen public identifier; never their raw email unless they
  // picked the email type (getUserLabel enforces this).
  const friendLabel = getUserLabel(friend) || "this friend";
  const viewerId = friend?.uid || null;

  const closeIfNotBusy = (next) => {
    if (busy) return;
    onOpenChange(next);
  };

  const handleGrant = async () => {
    if (!ownerId || !viewerId) {
      toast.error("Cannot share — missing user info. Try again in a moment.");
      return;
    }
    setBusy(true);
    try {
      await grantAvailabilityShare({
        ownerId,
        viewerId,
        redactMarkTitles,
      });
      toast.success(`Sharing your availability with ${friendLabel}`);
      onOpenChange(false);
    } catch (err) {
      console.error("Failed to grant availability share:", err);
      toast.error(err?.message || "Failed to share availability");
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!ownerId || !viewerId) return;
    setBusy(true);
    try {
      await updateAvailabilityShare({
        ownerId,
        viewerId,
        redactMarkTitles,
      });
      toast.success("Sharing settings updated");
      onOpenChange(false);
    } catch (err) {
      console.error("Failed to update availability share:", err);
      toast.error(err?.message || "Failed to update sharing settings");
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!ownerId || !viewerId) return;
    setBusy(true);
    try {
      await revokeAvailabilityShare({ ownerId, viewerId });
      toast.success(`Stopped sharing availability with ${friendLabel}`);
      onOpenChange(false);
    } catch (err) {
      console.error("Failed to revoke availability share:", err);
      toast.error(err?.message || "Failed to revoke sharing");
    } finally {
      setBusy(false);
    }
  };

  return (
    <SimpleModal open={open} onOpenChange={closeIfNotBusy}>
      <div className="max-w-md">
        <SimpleModalHeader>
          <SimpleModalTitle>
            {mode === "grant"
              ? `Share availability with ${friendLabel}`
              : `Manage sharing with ${friendLabel}`}
          </SimpleModalTitle>
          <SimpleModalDescription>
            {mode === "grant" ? (
              <>
                {friendLabel} will be able to overlay your redacted
                availability when creating session polls you’re invited to.
                Calendar event titles, descriptions, and locations are
                <strong> never </strong>
                shared.
              </>
            ) : (
              <>
                You’re currently sharing your availability with {friendLabel}.
                Update the redaction toggle below or revoke access.
              </>
            )}
          </SimpleModalDescription>
        </SimpleModalHeader>

        <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p
                id="redact-mark-titles-label"
                className="text-sm font-semibold text-slate-700 dark:text-slate-200"
              >
                Redact mark titles
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                When ON, {friendLabel} sees only “Preferred” / “Feasible”
                labels — the title you wrote on each free-time mark stays
                private.
              </p>
            </div>
            <Switch
              aria-labelledby="redact-mark-titles-label"
              checked={redactMarkTitles}
              onCheckedChange={(value) => setRedactMarkTitles(Boolean(value))}
              disabled={busy}
            />
          </div>
        </div>

        <SimpleModalFooter className="mt-6">
          {mode === "manage" && (
            <button
              type="button"
              onClick={handleRevoke}
              disabled={busy}
              className="rounded-full border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/30"
            >
              Revoke access
            </button>
          )}
          <button
            type="button"
            onClick={() => closeIfNotBusy(false)}
            disabled={busy}
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          {mode === "grant" ? (
            <button
              type="button"
              onClick={handleGrant}
              disabled={busy || !viewerId}
              className="rounded-full bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:opacity-50"
            >
              {busy ? "Sharing…" : "Share"}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="rounded-full bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save changes"}
            </button>
          )}
        </SimpleModalFooter>
      </div>
    </SimpleModal>
  );
}
